import EditOutlined from '@ant-design/icons/EditOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Col from 'antd/lib/col';
import Divider from 'antd/lib/divider';
import Form, { FormInstance } from 'antd/lib/form';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Modal from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Switch from 'antd/lib/switch';
import Typography from 'antd/lib/typography';
import { cloneDeep, isArray, isNumber, isBoolean } from 'lodash';
import inRange from 'lodash/inRange';
import { useContext, useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../domain/auth/useAuth';
import { COMPANY_TYPE, ICompany } from '../../domain/company/interface';
import { parseGooglePlace } from '../../domain/location';
import { checkIfCanUseSan, checkForSiteDuplicate } from '../../domain/site';
import { ICreateSite, IMeter, ISanFormItem, ISite, IUpdateSite, IEnrollSiteToPrograms } from '../../domain/site/interface';
import { useSiteCreateMutation, useSiteUpdateMutation } from '../../domain/site/queries';
import { suggestSANFormat } from '../../domain/site/suggestSANFormat';
import { handleLettersNumberInput } from '../../pages/sites/siteInterface';
import { AbilityContext } from '../ability/can';
import { filterCompaniesByType, transformCompaniesToSelectOption } from '../selectors/GlobalCompanySelector/companyHelpers';
import { filterSelectorOption } from '../selectors/selectorHelpers';
import { SmartMarketZoneSelector } from '../selectors/SmartMarketZoneSelector/SmartMarketZoneSelector';
import { UtilitySelector } from '../selectors/UtilitySelector/UtilitySelector';
import { ManageSanModal } from './ManageSanModal';
import { PartnerIdStatusAlert } from './PartnerIdStatusAlert';
import { SanDetailsList } from './SanDetailsList';
import { ISiteFormData, SiteFormData } from './SiteFormData';
import { SiteLabelsSection } from './SiteLabelsSection';
import { SiteSanFormData } from './SiteSanFormData';
import { MeterList } from './MeterList';
import { ManageMeterModal } from './ManageMeterModal';
import { EnrollProgramModal } from './EnrollProgramModal';
import { IProgram } from '../../domain/program/interface';
import Flex from 'antd/lib/flex';
import Empty from 'antd/lib/empty';
import { SiteEnrollments } from './SiteEnrollments';
import { checkIfLoadZoneSupportsPriceResponse } from '../../domain/site/useMarketZoneOptions';
import { supportedMarkets } from '../../domain/market-prices/interface';
import { PlacesAutocomplete } from '../PlacesAutocomplete';

interface ISiteFormModalProps {
    companies: ICompany[];
    programs: IProgram[];
    site: ISite | Partial<ISite>;
    onClose: (site?: Partial<ISite>, closeModal?: boolean) => void;
}

export function SiteFormModal({ site, companies, programs, onClose }: ISiteFormModalProps) {
    const { notification } = App.useApp();
    const [form] = Form.useForm<ISiteFormData>();

    const auth = useAuth()!;
    const isCustomerRoleType = auth.user?.isCustomer();
    const ability = useContext(AbilityContext);
    const customerCompanies = filterCompaniesByType(companies, COMPANY_TYPE.CUSTOMER_COMPANY);
    const controlProviderCompanies = filterCompaniesByType(companies, COMPANY_TYPE.CONTROL_PROVIDER);
    const utilityCustomerCompanies = filterCompaniesByType(companies, COMPANY_TYPE.UTILITY_CUSTOMER);

    const [place, setPlace] = useState(getPlaceFromSite(site));

    const [state, setState] = useState({
        isManageSansModalOpen: false,
        siteSans: site.sans?.map(SiteSanFormData.fromEntity) || [],
        labels: site.site_label || [],
    });

    const companyId = Form.useWatch('company_id', form);
    const utility = Form.useWatch('site_utility', form);
    const marketZone = Form.useWatch('site_market_and_zone', form);
    const siteId = Form.useWatch('site_id', form);
    const address = Form.useWatch('site_address', form);
    const market = marketZone?.[0] || site?.lmp_market || null;

    const isNewSite = !siteId;

    const siteCreateMutation = useSiteCreateMutation();
    const siteUpdateMutation = useSiteUpdateMutation();

    const isLoading = siteCreateMutation.isLoading || siteUpdateMutation.isLoading;

    const [isManageMetersModalOpen, setIsManageMetersModalOpen] = useState<boolean>(false);
    const [isManageEnrollmentsModalOpen, setIsManageEnrollmentsModalOpen] = useState<boolean>(false);

    const [selectedMeters, setSelectedMeters] = useState<IMeter[]>(site.meters || []);
    const [selectedEnrollments, setSelectedEnrollments] = useState<IEnrollSiteToPrograms>({});

    const isEnrollmentSelected = useMemo(() => isAnyEnrollmentSelected(selectedEnrollments), [selectedEnrollments]);

    useEffect(() => {
        if (site.site_id) {
            form?.validateFields(['site_store_number']);
            form?.validateFields(['site_address']);
        }
    }, []);

    const closeManageMetersModal = () => {
        setIsManageMetersModalOpen(false);
    };

    const openManageMetersModal = () => {
        setIsManageMetersModalOpen(true);
    };

    const handleManageMetersFinish = (updatedMeters: IMeter[]) => {
        const updatedMetersIds = updatedMeters.map(meter => meter.salesforce_id);

        setSelectedMeters(updatedMeters);
        form.setFieldValue('meter_ids', updatedMetersIds);
        setIsManageMetersModalOpen(false);
    };

    const handleUnlinkMeters = (salesforceId: string) => {
        const updatedMeters = selectedMeters.filter(meter => meter.salesforce_id !== salesforceId);
        const updatedMetersIds = updatedMeters.map(meter => meter.salesforce_id) || [];

        setSelectedMeters(updatedMeters);
        form.setFieldValue('meter_ids', updatedMetersIds);
    };

    const onEnrollmentModalClose = (siteId?: number, value?: IEnrollSiteToPrograms) => {
        if (value) {
            const updatedEnrollments: IEnrollSiteToPrograms = {
                programs: value.programs,
                // Only update state when values are updated. Avoid setting it to undefined
                price_response: isBoolean(value.price_response) ? value.price_response : selectedEnrollments.price_response,
                clean_response: isBoolean(value.clean_response) ? value.clean_response : selectedEnrollments.clean_response,
            };
            setSelectedEnrollments(updatedEnrollments);
            verifyProgramsList(form.getFieldValue('utility_customer_id'));
        }

        setIsManageEnrollmentsModalOpen(false);
    };

    async function onSiteModalFormFinish(siteFormData: ISiteFormData) {
        try {
            let preparedSite = SiteFormData.toEntity(siteFormData, auth.user!);
            preparedSite.site_label = state.labels;
            preparedSite.sans = state.siteSans;

            if (!isNewSite) {
                await siteUpdateMutation.mutateAsync(preparedSite as IUpdateSite);
            } else {
                const { programs, price_response, clean_response } = selectedEnrollments;

                if (programs?.length) preparedSite.program_ids = programs;
                if (price_response) preparedSite.site_price_response_enrolled = true;
                if (clean_response) preparedSite.site_clean_response_enrolled = true;

                await siteCreateMutation.mutateAsync(preparedSite as ICreateSite);
            }

            notification.success({ key: 'site-save-info', message: 'Site saved' });
            onClose(site);
        } catch (error: any) {
            notification.error({ key: 'site-save-info', message: error?.message || 'Cannot save site!' });
        }
    }

    const handleManageSansModalOpen = (value: boolean) => {
        setState({
            ...state,
            isManageSansModalOpen: value,
        });
    };

    const handleLabelsChange = (value: string[]) => {
        setState({
            ...state,
            labels: value,
        });
    };

    function handleSuggestionZoneClick(value: string | string[] | undefined) {
        form.setFieldValue('site_market_and_zone', value);
        form.validateFields(['site_market_and_zone']);
    }

    // Remove special programs (Clean response/Prise response) if market is changed to unsupported
    function handleMarketZoneChange(value: string | string[] | undefined) {
        if (!isNewSite || !isArray(value)) return;

        const { price_response, clean_response } = selectedEnrollments;

        if (!price_response && !clean_response) return;

        const [market, zone] = value;

        const isPriceResponseSupported = checkIfLoadZoneSupportsPriceResponse(market, zone);
        const isCleanResponseSupported = supportedMarkets.includes(market.toLowerCase());

        const updatedEnrollments = { ...selectedEnrollments };

        if (price_response && !isPriceResponseSupported) {
            notification.warning({
                key: 'price_response_not_supported',
                message: `Price Response program is not supported for ${market}/${zone} load zone!`,
            });
            updatedEnrollments.price_response = false;
        }

        if (clean_response && !isCleanResponseSupported) {
            notification.warning({
                key: 'clean_response_not_supported',
                message: `Clean Response program is not supported for ${market} market!`,
            });
            updatedEnrollments.clean_response = false;
        }

        setSelectedEnrollments(updatedEnrollments);
    }

    // Remove utility customer programs if site is assigned to another not related utility customer
    function verifyProgramsList(utilityCustomerId: string | string[] | undefined) {
        if (!isNewSite || !isNumber(utilityCustomerId)) return;

        const { programs: selectedProgramIds } = selectedEnrollments;

        if (!selectedProgramIds?.length) return;

        const selectedPrograms: IProgram[] = programs.filter(program => selectedProgramIds?.includes(program.program_id));

        const filteredList: IProgram[] = selectedPrograms.filter(
            program => !program.utility_customer_id || program.utility_customer_id === utilityCustomerId
        );

        if (selectedPrograms.length > filteredList.length) {
            notification.warning({
                key: 'utility_customer_program_not_supported',
                message: `Can't attach program related to another utility customer!`,
            });
            setSelectedEnrollments({ ...selectedEnrollments, programs: filteredList.map(program => program.program_id) });
        }
    }

    function handleUtilityChange(value: string | string[]) {
        form.setFieldValue('site_utility', value);
    }

    function closeSiteDialog() {
        onClose();
    }

    function handleSansUpdated(updatedSans: ISanFormItem[]) {
        setState(prevState => ({
            ...prevState,
            siteSans: updatedSans,
            isManageSansModalOpen: false,
        }));
    }

    function handleSansModalClose() {
        handleManageSansModalOpen(false);
    }

    const SiteAddressForm = {
        resetHiddenFields() {
            form.setFieldsValue({ site_city: undefined });
            form.setFieldsValue({ site_state: undefined });
            form.setFieldsValue({ site_country: undefined });
            form.setFieldsValue({ site_lat: undefined });
            form.setFieldsValue({ site_long: undefined });
            form.setFieldsValue({ site_zip: undefined });
            form.setFieldsValue({ site_timezone: null });
            form.setFieldsValue({ google_place_id: null });
            form.setFieldsValue({ site_county: null });
        },

        async onSelect(place: google.maps.GeocoderResult | null) {
            const parsed = await parseGooglePlace(place);
            console.info(`SiteAddressForm.onSelect parsed=`, parsed);

            setPlace({
                site_lat: parsed?.site_lat!,
                site_long: parsed?.site_long!,
                site_zip: parsed?.site_zip!,
                site_county: parsed?.site_county,
            });

            if (!parsed?.site_address) {
                SiteAddressForm.resetHiddenFields();
                return;
            }

            form.setFieldsValue({ site_address: parsed?.site_address });
            form.setFieldsValue({ site_city: parsed.site_city! });
            form.setFieldsValue({ site_state: parsed.site_state! });
            form.setFieldsValue({ site_country: parsed?.site_country! });
            form.setFieldsValue({ site_lat: parsed?.site_lat! });
            form.setFieldsValue({ site_long: parsed?.site_long! });
            form.setFieldsValue({ site_zip: parsed?.site_zip! });
            form.setFieldsValue({ site_timezone: parsed?.site_timezone });
            form.setFieldsValue({ google_place_id: parsed?.google_place_id });
            form.setFieldsValue({ site_county: parsed?.site_county });

            if (!parsed?.site_zip) {
                form.setFields([
                    {
                        name: 'site_address',
                        errors: ['Address should contain zip code'],
                    },
                ]);
            }

            if (!parsed?.site_timezone) {
                form.setFields([
                    {
                        name: 'site_timezone',
                        errors: ['Cannot get a timezone for this address'],
                    },
                ]);
            }

            form.validateFields(['site_address']);
        },

        onChange() {
            this.resetHiddenFields();
        },

        correctAddressValidator({ getFieldValue }: { getFieldValue: Function }) {
            return {
                async validator(_: any, value: any) {
                    if (value === '') return Promise.resolve();

                    const allReqSiteAddressFieldsComplete =
                        getFieldValue('site_address') &&
                        getFieldValue('site_city') &&
                        getFieldValue('site_state') &&
                        getFieldValue('site_country') &&
                        getFieldValue('site_lat') &&
                        getFieldValue('site_long') &&
                        getFieldValue('site_zip') &&
                        getFieldValue('site_timezone');

                    if (!allReqSiteAddressFieldsComplete) {
                        return Promise.reject(new Error('Address is not correct!'));
                    }

                    return Promise.resolve();
                },
            };
        },

        duplicateAddressValidator({ getFieldValue }: { getFieldValue: Function }) {
            return {
                warningOnly: true,
                async validator(_: any, value: any) {
                    if (value === '' || !companyId) return Promise.resolve();

                    const allReqSiteAddressFieldsComplete =
                        getFieldValue('site_address') &&
                        getFieldValue('site_city') &&
                        getFieldValue('site_state') &&
                        getFieldValue('site_country') &&
                        getFieldValue('site_lat') &&
                        getFieldValue('site_long') &&
                        getFieldValue('site_zip') &&
                        getFieldValue('site_timezone');

                    if (!allReqSiteAddressFieldsComplete) {
                        return Promise.resolve();
                    }

                    let { duplicatedSites } = await checkForSiteDuplicate(companyId, [
                        {
                            site_address: getFieldValue('site_address'),
                            site_city: getFieldValue('site_city'),
                            site_state: getFieldValue('site_state'),
                            site_zip: getFieldValue('site_zip'),
                            site_country: getFieldValue('site_country'),
                            google_place_id: getFieldValue('google_place_id'),
                        },
                    ]);

                    if (!isNewSite) {
                        duplicatedSites = duplicatedSites.filter(site => site.site_id !== siteId);
                    }

                    if (duplicatedSites.length) {
                        return Promise.reject(new Error(buildDuplicatedSitesWarningMessage(duplicatedSites, 'address')));
                    }

                    return Promise.resolve();
                },
            };
        },
    };

    const onCompanySelect = () => {
        form.validateFields(['site_store_number']);
        if (address) {
            form.validateFields(['site_address']);
        }
    };

    async function checkSiteUtilityCustomer(val: number | undefined) {
        if (typeof val === 'undefined') {
            return Promise.reject();
        }

        return Promise.resolve();
    }

    return (
        <Modal
            open
            title={site?.site_id ? 'Edit site' : 'New site'}
            destroyOnHidden
            onCancel={closeSiteDialog}
            footer={[
                <Button key="site-modal-cancel" onClick={closeSiteDialog}>
                    Cancel
                </Button>,
                <Button key="site-modal-submit" data-cy="save-site" type="primary" loading={isLoading} onClick={form.submit}>
                    Save
                </Button>,
            ]}
            data-cy="site-modal"
        >
            <Form
                form={form}
                name="site-form"
                preserve={false}
                layout="vertical"
                onFinish={onSiteModalFormFinish}
                initialValues={SiteFormData.fromEntity(site)}
            >
                <Form.Item name="site_id" hidden>
                    <Input data-cy="site-id" />
                </Form.Item>

                {!isCustomerRoleType && (
                    <Form.Item
                        name="company_id"
                        label={<Typography.Text strong>Company</Typography.Text>}
                        hasFeedback
                        rules={[{ required: true, message: 'Please select company!' }]}
                    >
                        <Select
                            showSearch
                            onChange={onCompanySelect}
                            data-cy="company-selector"
                            size="large"
                            placeholder="Please select company"
                            options={transformCompaniesToSelectOption(customerCompanies) as any}
                            filterOption={filterSelectorOption}
                        />
                    </Form.Item>
                )}

                <Form.Item
                    name="site_name"
                    label={<Typography.Text strong>Site Name</Typography.Text>}
                    hasFeedback
                    rules={[
                        { required: true, message: 'Please enter name!' },
                        { max: 200, message: 'Number of characters should be less than 200' },
                    ]}
                >
                    <Input data-cy="site-name" placeholder="Site name" size="large" />
                </Form.Item>

                <Form.Item
                    shouldUpdate
                    name="site_address"
                    label={<Typography.Text strong>Address</Typography.Text>}
                    hasFeedback
                    rules={[
                        { required: true, message: 'Please enter address!' },
                        { max: 200, message: 'Number of characters should be less than 200' },
                        SiteAddressForm.correctAddressValidator,
                        SiteAddressForm.duplicateAddressValidator,
                    ]}
                >
                    <PlacesAutocomplete onSelect={SiteAddressForm.onSelect} />
                </Form.Item>

                <Form.Item name="site_city" hidden>
                    <Input placeholder="" />
                </Form.Item>
                <Form.Item name="site_state" hidden>
                    <Input placeholder="" />
                </Form.Item>
                <Form.Item name="site_zip" hidden>
                    <Input placeholder="" />
                </Form.Item>
                <Form.Item name="site_country" hidden>
                    <Input placeholder="" />
                </Form.Item>
                <Form.Item name="site_county" hidden>
                    <Input placeholder="" />
                </Form.Item>
                <Form.Item name="site_lat" hidden>
                    <Input placeholder="" />
                </Form.Item>
                <Form.Item name="site_long" hidden>
                    <Input placeholder="" />
                </Form.Item>
                <Form.Item name="site_timezone" hidden data-cy="site-timezone">
                    <Input placeholder="" />
                </Form.Item>
                <Form.Item name="google_place_id" hidden>
                    <Input placeholder="" />
                </Form.Item>

                <Form.Item
                    validateDebounce={500}
                    rules={[
                        { max: 12, message: 'Number of characters should be less than 12' },
                        {
                            validator: (_, value) => checkSiteStoreNumber(_, value, site, form),
                            warningOnly: true,
                        },
                    ]}
                    name="site_store_number"
                    label={<Typography.Text strong>Store Number</Typography.Text>}
                >
                    <Input placeholder="Store Number" size="large" style={{ width: '100%' }} />
                </Form.Item>

                {auth.user?.isAdminRoleType() && (
                    <>
                        <Divider plain>Salesforce</Divider>

                        <Form.Item
                            name="meter_ids"
                            label={
                                <>
                                    <Typography.Text strong>Linked Salesforce Meters</Typography.Text>
                                    <Button size="small" type="link" onClick={openManageMetersModal}>
                                        Manage <EditOutlined />
                                    </Button>
                                </>
                            }
                        >
                            <MeterList meters={selectedMeters} onUnlinkMeter={handleUnlinkMeters} />
                        </Form.Item>
                    </>
                )}

                <Divider plain>Utility</Divider>

                <Row gutter={8}>
                    <Col span={24}>
                        <Form.Item
                            label={
                                <>
                                    <Typography.Text strong>Enrollment ID Details</Typography.Text>

                                    <Button type="link" onClick={() => handleManageSansModalOpen(true)}>
                                        Edit <EditOutlined />
                                    </Button>
                                </>
                            }
                        >
                            <SanDetailsList utility={utility} market={market} siteId={site.site_id || null} sans={state.siteSans} />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item noStyle dependencies={['site_zip']}>
                            {({ getFieldValue }) => {
                                const selectedZip = getFieldValue('site_zip');
                                const utility = getFieldValue('site_utility');

                                return (
                                    <Form.Item
                                        name="site_utility"
                                        rules={[{ max: 100, message: 'Number of characters should be less than 100' }]}
                                        label={<Typography.Text strong>Utility</Typography.Text>}
                                    >
                                        <UtilitySelector value={utility} onChange={handleUtilityChange} zip={selectedZip} />
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item hasFeedback name="site_market_and_zone" label={<Typography.Text strong>Load Zone</Typography.Text>}>
                            <SmartMarketZoneSelector
                                onChange={handleMarketZoneChange}
                                onSuggestionClick={handleSuggestionZoneClick}
                                selectedValues={marketZone}
                                zipcode={place.site_zip}
                                lat={place.site_lat}
                                long={place.site_long}
                                county={place.site_county}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="site_network"
                            rules={[{ max: 100, message: 'Number of characters should be less than 100' }]}
                            label={<Typography.Text strong>Network</Typography.Text>}
                        >
                            <Input placeholder="Network" size="large" />
                        </Form.Item>
                    </Col>

                    {auth.user?.isAdmin() && (
                        <Col span={12}>
                            <Form.Item
                                name="site_resource_id"
                                rules={[{ max: 100, message: 'Number of characters should be less than 100' }]}
                                label={<Typography.Text strong>OpenADR Resource ID</Typography.Text>}
                                tooltip="A unique identifier for this site used in OpenADR protocol messages exchanged with Customer VENs"
                            >
                                <Input placeholder="OpenADR Resource ID" size="large" />
                            </Form.Item>
                        </Col>
                    )}

                    {auth.user?.isAdmin() && (
                        <Col span={12}>
                            <Form.Item
                                name="utility_resource_id"
                                rules={[{ max: 255, message: 'Number of characters should be less than 255' }]}
                                label={<Typography.Text strong>Utility Resource ID</Typography.Text>}
                                tooltip="A unique identifier assigned by the utility to this site, used in Cloud VEN data exchange"
                            >
                                <Input placeholder="Utility Resource ID" size="large" />
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                {ability.can('attach', 'Program') && isNewSite && (
                    <>
                        <Divider plain>Programs</Divider>

                        <Form.Item
                            name="programs"
                            label={
                                <>
                                    <Typography.Text strong>Program Details</Typography.Text>
                                    <Button size="small" type="link" onClick={() => setIsManageEnrollmentsModalOpen(true)}>
                                        Manage <EditOutlined />
                                    </Button>
                                </>
                            }
                        >
                            <div>
                                {isEnrollmentSelected ? (
                                    <SiteEnrollments value={siteEnrollmentToSite(selectedEnrollments, programs) as ISite} />
                                ) : (
                                    <Flex style={{ height: 90 }} justify="center" align="center">
                                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Link Programs to see them in list" />
                                    </Flex>
                                )}
                            </div>
                        </Form.Item>
                    </>
                )}

                {ability.can('attach', 'UtilityCustomer') && (
                    <Form.Item
                        name="utility_customer_id"
                        label={<Typography.Text strong>Utility Program Eligibility</Typography.Text>}
                        tooltip="Programs added here are separate from market programs and are a collaboration between the utility and Enersponse"
                        rules={[
                            {
                                message: 'Removing eligibility will remove this Site from the Utility program. Are you sure you want to do this?',
                                validator: (_, value) => checkSiteUtilityCustomer(value),
                                warningOnly: true,
                            },
                        ]}
                    >
                        <Select
                            allowClear
                            placeholder="None"
                            size="large"
                            onChange={verifyProgramsList}
                            options={transformCompaniesToSelectOption(utilityCustomerCompanies) as any}
                        />
                    </Form.Item>
                )}

                <Divider plain>Controls</Divider>

                <Form.Item label={<Typography.Text strong>Partner ID</Typography.Text>}>
                    <Row gutter={[8, 8]}>
                        <Col span={24}>
                            <Form.Item noStyle name="partner_id">
                                <Input size="large" placeholder="Partner ID" />
                            </Form.Item>
                        </Col>
                        <PartnerIdStatusAlert partner_id_status={site.partner_id_status!} />
                    </Row>
                </Form.Item>

                {ability.can('attach', 'ControlProvider') && (
                    <Form.Item name="control_provider_id" label={<Typography.Text strong>Control Provider</Typography.Text>}>
                        <Select
                            allowClear
                            placeholder="Please select Control Provider"
                            size="large"
                            options={transformCompaniesToSelectOption(controlProviderCompanies) as any}
                        />
                    </Form.Item>
                )}

                {auth.user?.isAdminRoleType() && (
                    <>
                        <Form.Item
                            name="site_estimated_kw"
                            label={<Typography.Text strong>Site estimated KW</Typography.Text>}
                            rules={[
                                {
                                    validator(_, value) {
                                        if (!value) {
                                            form.setFieldsValue({ site_estimated_kw: 0 });
                                            return Promise.resolve();
                                        }
                                        if (inRange(value, 0, 100001)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Value should be in range[0-100000]'));
                                    },
                                },
                            ]}
                        >
                            <InputNumber type="number" min={0} max={100000} size="large" onKeyPress={handleLettersNumberInput} />
                        </Form.Item>

                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    label={<Typography.Text strong>Event maximum duration</Typography.Text>}
                                    tooltip={
                                        <>
                                            <p style={{ marginBottom: 0 }}>Allow a maximum duration to be applied to the event time.</p>
                                            <p>Allowed interval is 0-4 hours</p>
                                        </>
                                    }
                                >
                                    <Space>
                                        <Form.Item name="event_max_duration" noStyle>
                                            <InputNumber min={0} max={4} step={0.25} size="large" />
                                        </Form.Item>
                                        <Typography.Text>hours</Typography.Text>
                                    </Space>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={<Typography.Text strong>Event Offset</Typography.Text>}
                                    tooltip={
                                        <>
                                            <p style={{ marginBottom: 0 }}>
                                                Allow an offset to be applied to the event time to start earlier than scheduled.
                                            </p>
                                            <p style={{ marginTop: 0 }}>Allowed interval is 0-30 min</p>
                                        </>
                                    }
                                >
                                    <Space>
                                        <Form.Item
                                            noStyle
                                            name="event_offset"
                                            rules={[
                                                {
                                                    validator(_, value) {
                                                        if (!value) {
                                                            form.setFieldsValue({ event_offset: 0 });
                                                            return Promise.resolve();
                                                        }

                                                        if (inRange(value, 0, 31)) {
                                                            return Promise.resolve();
                                                        }

                                                        return Promise.reject(new Error('Value should be in range[0-30]'));
                                                    },
                                                },
                                            ]}
                                        >
                                            <InputNumber type="number" size="large" onKeyPress={handleLettersNumberInput} />
                                        </Form.Item>
                                        <Typography.Text>min</Typography.Text>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider plain>Features</Divider>

                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    name="site_highlight"
                                    valuePropName="checked"
                                    label={<Typography.Text strong>Highlight site</Typography.Text>}
                                >
                                    <Switch checked={site.site_highlight} />
                                </Form.Item>
                            </Col>
                        </Row>

                        {auth.user?.isAdminRoleType() && (
                            <Row gutter={8}>
                                <Col span={24}>
                                    <SiteLabelsSection
                                        allowCreate
                                        siteLabels={state.labels}
                                        setSiteLabels={handleLabelsChange}
                                        companyId={companyId}
                                    />
                                </Col>
                            </Row>
                        )}
                    </>
                )}
            </Form>

            {state.isManageSansModalOpen && (
                <ManageSanModal
                    siteId={site.site_id || null}
                    market={market}
                    utility={utility}
                    sans={cloneDeep(state.siteSans)}
                    onCancel={handleSansModalClose}
                    onOk={handleSansUpdated}
                />
            )}

            {isManageMetersModalOpen && (
                <ManageMeterModal
                    site={site.site_id ? (site as ISite) : null}
                    meters={selectedMeters}
                    onClose={closeManageMetersModal}
                    onOk={handleManageMetersFinish}
                />
            )}

            {ability.can('enroll', 'Program') && isManageEnrollmentsModalOpen && (
                <EnrollProgramModal
                    programs={programs}
                    site={siteEnrollmentToSite(selectedEnrollments, programs, form.getFieldValue('site_market_and_zone'))}
                    loading={false}
                    onClose={onEnrollmentModalClose}
                />
            )}
        </Modal>
    );
}

function getPlaceFromSite(site: ISite | Partial<ISite>) {
    return {
        site_lat: site?.site_lat,
        site_long: site?.site_long,
        site_zip: site?.site_zip,
        site_county: site?.site_county,
    };
}

export function validateSanFormat(_: any, san: string, utility: string | null, market: string | null) {
    if (!san) return Promise.resolve();

    const suggestion = suggestSANFormat(san, utility, market);

    if (!suggestion || suggestion.isValid) return Promise.resolve();

    return Promise.reject(new Error(suggestion.suggestedFormat));
}

export async function validateIfSanAvailable(_: any, san: string, siteId: number | null) {
    if (!san) return Promise.resolve();

    const canUseSan = await checkIfCanUseSan(san, siteId);

    if (canUseSan) return Promise.resolve();

    return Promise.reject();
}

export async function checkSiteStoreNumber(_: any, storeNumber: string, site: ISite | Partial<ISite>, form: FormInstance<ISiteFormData>) {
    if (!storeNumber) {
        return Promise.resolve();
    }

    const companyId = form.getFieldValue('company_id');

    if (!companyId) {
        return Promise.resolve();
    }

    let { duplicatedSites } = await checkForSiteDuplicate(companyId, [{ site_store_number: storeNumber }]);

    if (site.site_id) {
        duplicatedSites = duplicatedSites.filter(duplicatedSite => duplicatedSite.site_id !== site.site_id);
    }

    if (duplicatedSites.length) {
        return Promise.reject(buildDuplicatedSitesWarningMessage(duplicatedSites, 'store number'));
    }
    return Promise.resolve();
}

function buildDuplicatedSitesWarningMessage(sites: ISite[], filedName: string) {
    const isMultipleSites = sites.length > 1;
    const siteNames = sites.map(site => `'${site.site_name}'`);
    return `Site${isMultipleSites ? 's' : ''} ${siteNames.join(', ')} with this ${filedName} already exist${isMultipleSites ? '' : 's'}`;
}

function siteEnrollmentToSite(enrollment: IEnrollSiteToPrograms, allPrograms: IProgram[], siteMarketZone?: string[]): Partial<ISite> {
    const site: Partial<ISite> = {
        site_price_response_enrolled: !!enrollment.price_response,
        site_clean_response_enrolled: !!enrollment.clean_response,
        site_leap_api_dispatch_enrolled: !!enrollment.leap_api_dispatch,
    };

    if (enrollment.programs?.length) {
        site.programs = allPrograms.filter(program => enrollment.programs?.includes(program.program_id));
    }

    if (siteMarketZone) {
        site.lmp_market = siteMarketZone[0] || undefined;
        site.site_load_zone = siteMarketZone[1] || undefined;
    }

    return site;
}

function isAnyEnrollmentSelected(enrollment: IEnrollSiteToPrograms): boolean {
    const { programs, price_response, clean_response } = enrollment;

    return Boolean(programs?.length || price_response || clean_response);
}
